import type { TutorBillingTableColumn, TutorBillingTableRow } from './interfaces'

import { useListsStore } from '@shared/model'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingErase } from '../api/erase.hook'
import { useTutorBillingListApi } from '../api/list.hook'
import { buildTutorBillingFields, TUTOR_BILLING_EMPTY_VALUE } from '../lib/tutor-billing-fields'

interface ModalTutorBillingEraseReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  columns: TutorBillingTableColumn[]
  rows: ComputedRef<TutorBillingTableRow[]>
  setKeep: () => void
  setErase: () => Promise<void>
}

const COLUMNS: TutorBillingTableColumn[] = [
  { key: 'now', label: 'Now' },
  { key: 'after', label: 'After erasing' },
]

const KEPT_AFTER_ERASE = new Set<string>(['id', 'tutor', 'email', 'reference_number'])

export const useModalTutorBillingErase = (): ModalTutorBillingEraseReturn => {
  const store = useTutorBillingStore()
  const listsStore = useListsStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.Erase))
  const isSaving = computed<boolean>(() => store.isSaving)

  const rows = computed<TutorBillingTableRow[]>(() => {
    const row = store.getActiveRow
    if (!row) return []

    return buildTutorBillingFields(row, listsStore.getTutorBillingPaymentServiceLabel).map(field => ({
      key: field.key,
      label: field.label,
      cells: [
        { value: field.value },
        { value: KEPT_AFTER_ERASE.has(field.key) ? field.value : TUTOR_BILLING_EMPTY_VALUE, tone: 'muted' },
      ],
    }))
  })

  const setKeep = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setErase = async (): Promise<void> => {
    const rowId = store.getActiveRow?.id
    if (!rowId || store.isSaving) return

    const isErased = await useTutorBillingErase(rowId)
    if (!isErased) return

    store.deselect(rowId)
    if (store.isModalFor(TutorBillingModal.Erase, rowId)) store.closeModal()
    void useTutorBillingListApi()
  }

  return {
    isOpen,
    isSaving,
    columns: COLUMNS,
    rows,
    setKeep,
    setErase,
  }
}
